import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useAllCatalogProducts } from '/@/hooks/catalog';
import { getCatalogBrand } from '/@/store/catalog/selectors';
import { getSessionCountry, getSessionLanguage } from '/@/store/session/selectors';

export interface CropProtectionProductType {
    id: number;
    name?: string;
    es?: string;
    en?: string;
    pt?: string;
}

export interface CropProtectionCatalogProduct {
    id: number;
    name: string;
    type?: CropProtectionProductType | null;
}

const typeLabel = (type: CropProtectionProductType | null | undefined, language: string): string =>
    type?.name ?? type?.[language as 'es' | 'en' | 'pt'] ?? type?.en ?? '';

/**
 * Crop-protection catalog products for the active brand/country, plus the product types
 * derived from them (deduped by id). Used to feed the application-register product/type
 * selects and to resolve a type id back to its display name.
 */
export const useCropProtectionProducts = () => {
    const country = useSelector(getSessionCountry);
    const language = useSelector(getSessionLanguage);
    const brand = useSelector((state) => getCatalogBrand(state, 'crop_protection'));

    const query = useAllCatalogProducts(
        {
            brand: brand?.name ?? '',
            crop: 'crop_protection',
            country: country?.code ?? '',
            cropProtectionCrop: undefined,
            cropProtectionProductType: undefined,
        },
        { enabled: Boolean(brand?.name && country?.code) }
    );

    const products = (query.data?.results ?? []) as CropProtectionCatalogProduct[];

    const types = useMemo(() => {
        const byId = new Map<number, string>();
        for (const product of products) {
            if (product.type?.id != null && !byId.has(product.type.id)) {
                byId.set(product.type.id, typeLabel(product.type, language));
            }
        }
        return [...byId.entries()].map(([id, name]) => ({ id, name }));
    }, [products, language]);

    const typeNameById = useCallback(
        (id: number | null | undefined): string => (id != null && types.find((t) => t.id === id)?.name) || '',
        [types]
    );

    return { isLoading: query.isLoading, products, types, typeNameById };
};
